import React, { useEffect, useState } from "react";

import Container from "@mui/material/Container";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Button from "@mui/material/Button";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import InputLabel from "@mui/material/InputLabel";

import AlertTitle from "@mui/material/AlertTitle";
import Alert from "@mui/material/Alert";

import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";

import dayjs from "dayjs";
import { isMobile } from "react-device-detect";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import Grid from "@mui/material/Grid";

import AccordionTitle from "../../components/AccordionTitle";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import {ReactComponent as ExpandMoreIcon} from "../../icons/ExpandMore24.svg";

import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { doService } from "../../services/ServiceHelper";
import { getReportPassBasic } from "../../services/ReportService";

import TableRowNoResults from "../../components/TableRowNoResults";
import CreatePassDialog from "../../components/faculty/CreatePassDialog";

import { getPassTypes, deletePass } from "../../services/PassService";
import { showOKCancelDialog, showToast } from "../../components/ATPDialogs";

const ManageTeacherPasses = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_searchPerformed, setSearchPerformed] = useState(false);
  const [_results, setResults] = useState([]);
  const [_isCriteriaExpanded, setCriteriaExpanded] = useState(true);
  const [_isResultsExpanded, setResultsExpanded] = useState(true);

  const [_dateRange, setDateRange] = useState([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);
  const [_dialogCreatePassOpen, setDialogCreatePassOpen] = useState(false);
  const [_passTypes, setPassTypes] = useState([]);
  const [_passTypeEid, setPassTypeEid] = useState("XX");
  const [_orderBy, setOrderBy] = useState("approvedDate");
  const [_maxRows, setMaxRows] = useState("100");
  const [_dialogOpen, setDialogOpen] = React.useState(false);
  const [_maxWidth, setMaxWidth] = React.useState(400);
  const [data, setData] = React.useState("Not Found");

  useEffect(() => {
    initPassTypes();
    initUi();
  }, []);

  const initUi = () => {
    if (isMobile) {
      setMaxWidth(450);
    } else {
      setMaxWidth(750);
    }
  };

  const initPassTypes = () => {
    doService(getPassTypes(_school, _jwt), (result) => {
      const types = [];
      for (const type of result.data) {
        types.push({
          label: type.name,
          value: type.eid,
        });
      }
      setPassTypes(types);
    });
  };

  const toggleCriteriaExpanded = () => {
    if (_isCriteriaExpanded === true) {
      setCriteriaExpanded(false);
      return;
    }
    setCriteriaExpanded(true);
  };

  const toggleResultsExpanded = () => {
    if (_isResultsExpanded === true) {
      setResultsExpanded(false);
      return;
    }
    setResultsExpanded(true);
  };

  const handleSearch = () => {
    const dateStart = _dateRange[0].format("YYYY-MM-DD");
    const dateEnd = _dateRange[1].format("YYYY-MM-DD");

    setCriteriaExpanded(false);
    setShowProgress(true);
    doService(
      getReportPassBasic(
        _school,
        _jwt,
        dateStart,
        dateEnd,
        _passTypeEid,
        _orderBy,
        _maxRows
      ),
      (results) => {
        setSearchPerformed(true);
        setResults(results.data);
        setShowProgress(false);
      }
    );
  };

  const handleCreatePass = () => {
    setDialogCreatePassOpen(true);
  };

  const handleCreatePassClose = () => {
    setDialogCreatePassOpen(false);
  };

  const handleCreatePassOK = (students) => {
    setDialogCreatePassOpen(false);
  };

  const handleClickDelete = (hallPass) => {
    showOKCancelDialog("Delete Pass", `Click OK to delete pass.`, () => {
      doService(deletePass(_school, _jwt, hallPass.eid), (result) => {
        handleSearch();
        showToast("S", "Pass Deleted!");
      });
    });
  };

  const handleScan = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Manage Teacher Passes" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Alert severity="info">
          <AlertTitle>Click "Scan Student" to </AlertTitle>
        </Alert>

        <Button
          sx={{ mt: 1, mb: 1 }}
          variant="contained"
          className="button1"
          onClick={handleCreatePass}
        >
          Create Pass
        </Button>

        <Button
          sx={{ mt: 1, mb: 1, ml: 1 }}
          variant="contained"
          className="button2"
          onClick={handleScan}
        >
          Scan Student
        </Button>

        <Accordion
          defaultExpanded
          sx={{ mt: 1 }}
          expanded={_isCriteriaExpanded === true}
          onChange={() => toggleCriteriaExpanded()}
        >
          <AccordionSummary
            className="accordionHeader"
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionTitle text="Search Criteria" />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputLabel>Start and End Dates *:</InputLabel>

                <MobileDateRangePicker
                  defaultValue={[dayjs(), dayjs()]}
                  value={_dateRange}
                  sx={{ mt: 1, mb: 0 }}
                  onChange={(value) => setDateRange(value)}
                />
              </Grid>

              <Grid item xs={6}>
                <InputLabel>Pass Type *:</InputLabel>
                <Select
                  onChange={(event) => setPassTypeEid(event.target.value)}
                  value={_passTypeEid}
                  sx={{ mb: 1, mt: 1 }}
                  fullWidth
                >
                  <MenuItem key="XX" value="XX">
                    -- ALL TYPES --
                  </MenuItem>
                  {_passTypes.map((passType) => (
                    <MenuItem key={passType.eid} value={passType.eid}>
                      {passType.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6}>
                <InputLabel sx={{ mt: 0 }}>Order By *:</InputLabel>
                <Select
                  onChange={(event) => setOrderBy(event.target.value)}
                  value={_orderBy}
                  sx={{ mb: 1, mt: 1 }}
                  fullWidth
                >
                  <MenuItem key="approvedDate" value="approvedDate">
                    Approved Date
                  </MenuItem>
                  <MenuItem key="lastName" value="lastName">
                    Last Name
                  </MenuItem>
                </Select>
              </Grid>
              <Grid item xs={6}>
                <InputLabel sx={{ mt: 0 }}>Max Rows Returned *:</InputLabel>
                <Select
                  onChange={(event) => setMaxRows(event.target.value)}
                  value={_maxRows}
                  sx={{ mb: 1, mt: 1 }}
                  fullWidth
                >
                  <MenuItem key="100" value="100">
                    100
                  </MenuItem>
                  <MenuItem key="200" value="200">
                    200
                  </MenuItem>
                  <MenuItem key="500" value="500">
                    500
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>

            <Button
              sx={{ mt: 1 }}
              variant="contained"
              fullWidth
              className="button1"
              onClick={handleSearch}
            >
              Search
            </Button>
          </AccordionDetails>
        </Accordion>

        {_searchPerformed && (
          <>
            <Accordion
              defaultExpanded
              sx={{ mt: 1 }}
              expanded={_isResultsExpanded === true}
              onChange={() => toggleResultsExpanded()}
            >
              <AccordionSummary
                className="accordionHeader"
                expandIcon={<ExpandMoreIcon />}
              >
                <AccordionTitle text={`Search Results (${_results.length})`} />
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small" sx={{ mb: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Student Name / Type</TableCell>
                        <TableCell>From/To: (Teacher/Room Number)</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRowNoResults data={_results} />

                      {_results.map((result, i) => (
                        <TableRow>
                          <TableCell>
                            <b>
                              {result.student_last_name},{" "}
                              {result.student_first_name}
                            </b>
                            <br />
                            APPR:{" "}
                            {dayjs(result.approved_at).format("MM/DD hh:mm")}
                            <br />
                            TYPE: {result.type_name}
                          </TableCell>
                          <TableCell>
                            FROM: {result.from_teacher_last_name},{" "}
                            {result.from_teacher_first_name.substring(0, 1)}
                            <br />
                            {result.to_location_name != null && (
                              <>
                                TO LOC: {result.to_location_name}
                                <br />
                              </>
                            )}
                            {result.to_teacher_last_name != null && (
                              <>
                                TO TEA: {result.to_teacher_last_name},{" "}
                                {result.to_teacher_first_name.substring(0, 1)}
                                <br />
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            {result.created_by_last_name},{" "}
                            {result.created_by_first_name.substring(0, 1)}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="outlined"
                              onClick={() => handleClickDelete(result)}
                            >
                              DELETE
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>

      <CreatePassDialog
        open={_dialogCreatePassOpen}
        onClose={handleCreatePassClose}
        onOK={handleCreatePassOK}
      />

      <Dialog
        PaperProps={{
          sx: {
            maxWidth: _maxWidth,
            maxHeight: 600,
          },
        }}
        onClose={handleDialogClose}
        open={_dialogOpen}
        fullScreen={true}
      >
        <DialogTitle>Scan Student QR Code</DialogTitle>

        <DialogContent>
          {/* <QrReader
            onResult={(result, error) => {
              if (!!result) {
                setData(result?.text);
              }

              if (!!error) {
                console.info(error);
              }
            }}
            style={{ width: "100%" }}
          /> */}
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageTeacherPasses;
